import {
  AmplifyErrorObject,
  appAuthenticator,
  AuthenticatorUser,
  UserAttributes,
  PROMISE_REQUEST
} from '@futrli/shared-services';
import { Action, AnyAction, Dispatch } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import {
  GET_CURRENT_AUTHENTICATED_USER,
  SET_PREFERRED_MFA_TYPE,
  STORE_PREFERRED_MFA_TYPE,
  STORE_USER_ATTRIBUTES,
  UPDATE_CURRENT_AUTHENTICATED_USER,
  UPDATE_USER_ATTRIBUTES,
  VERIFY_USER_ATTRIBUTES,
  VERIFY_USER_ATTRIBUTES_SUBMIT
} from '../../auth/constants';
import { DispatchAny, StoreState } from '../../core/types';
import { StorageDataValue } from '../../storage/types';

/**
 * Stores the user atrributes on the browser storage.
 * @param {Dispatch<AnyAction>} dispatch
 * @returns {function}
 */
const storeUserAttributes = (dispatch: Dispatch<AnyAction>) => {
  return (attributes: UserAttributes) => {
    dispatch({ type: STORE_USER_ATTRIBUTES, payload: attributes });
  };
};

/**
 * Stores the user atrributes on the browser storage.
 * @param {Dispatch<AnyAction>} dispatch
 * @returns {function}
 */
const storePreferredMfaType = (dispatch: Dispatch<AnyAction>) => {
  return (user: AuthenticatorUser) => {
    dispatch({ type: STORE_PREFERRED_MFA_TYPE, payload: user });
  };
};

/**
 * Gets the current authenticated user
 * @returns {ThunkDispatch<StoreState, unknown, Action>} thunk dispatch function
 * @returns {Promise} authenticated user
 */
export const getCurrentAuthenticatedUser = (): DispatchAny => {
  /**
   * To be able to manage the getCurrentAuthenticatedUser action state properly, we have to call the
   * auth method directly (instead of using the api middleware) and call the different GET_CURRENT_AUTHENTICATED_USER
   * states manually outside of this function to have full control of the loading for the main container.
   */
  return async (dispatch: ThunkDispatch<StoreState, unknown, Action>) => {
    const user = await appAuthenticator.getCurrentAuthenticatedUser();

    await getAndStoreUserAttributes(user)(dispatch);

    return user;
  };
};

/**
 * Updates the redux store with the state of authenticated user action to pending state.
 * @returns {DispatchAny}
 */
export const setCurrentAuthenticatedUserPending = (): DispatchAny => {
  return (dispatch: ThunkDispatch<StoreState, unknown, Action>) => {
    dispatch({ type: GET_CURRENT_AUTHENTICATED_USER.PENDING });
  };
};

/**
 * Updates the redux store with the state of authenticated user action to fulfilled state.
 * @param {AuthenticatorUser} user
 * @returns {DispatchAny}
 */
export const setCurrentAuthenticatedUserFulfilled = (user: AuthenticatorUser): DispatchAny => {
  return (dispatch: ThunkDispatch<StoreState, unknown, Action>) => {
    dispatch({ type: GET_CURRENT_AUTHENTICATED_USER.FULFILLED, payload: user });
  };
};

/**
 * Updates the redux store authenticated user with given payload.
 * @param {AuthenticatorUser} user
 * @returns {DispatchAny}
 */
export const updateCurrentAuthenticatedUser = (user: AuthenticatorUser): DispatchAny => {
  return (dispatch: ThunkDispatch<StoreState, unknown, Action>) => {
    dispatch({ type: UPDATE_CURRENT_AUTHENTICATED_USER, payload: user });
  };
};

/**
 * Updates the redux store with the state of authenticated user action to rejected state.
 * @returns {DispatchAny}
 */
export const setCurrentAuthenticatedUserRejected = (): DispatchAny => {
  const notAuthenticatedUserError: AmplifyErrorObject = {
    code: 'NotAuthenticated',
    name: 'NotAuthenticated',
    // TODO: Check if it needs i18n
    message: 'not authenticated'
  };

  return (dispatch: ThunkDispatch<StoreState, unknown, Action>) => {
    dispatch({
      type: GET_CURRENT_AUTHENTICATED_USER.REJECTED,
      payload: notAuthenticatedUserError,
      error: true
    });
  };
};

/**
 * Gets the user attributes.
 * @param {AuthenticatorUser} user
 * @returns {Promise} user attributes
 */
export const getUserAttributes = async (user: AuthenticatorUser): Promise<UserAttributes> => {
  return appAuthenticator.getUserAttributes(user) as Promise<UserAttributes>;
};

export const getAndStoreUserAttributes = (user: AuthenticatorUser) => {
  return async (dispatch: ThunkDispatch<StoreState, unknown, Action>) => {
    const attributes: UserAttributes = await getUserAttributes(user);

    storeUserAttributes(dispatch)(attributes);

    return attributes;
  };
};

const getAndStoreUserMfa = (user: AuthenticatorUser) => {
  return async (dispatch: ThunkDispatch<StoreState, unknown, Action>) => {
    storePreferredMfaType(dispatch)(user);

    return user;
  };
};

/**
 * Updates the user attributes.
 * @param {UserAttributes} attributes
 * @returns {ThunkDispatch<StoreState, unknown, Action>} thunk dispatch function
 */
export const updateUserAttributes = (attributes: UserAttributes) => {
  return async (dispatch: ThunkDispatch<StoreState, unknown, Action>) => {
    const user: AuthenticatorUser = await appAuthenticator.getCurrentAuthenticatedUser();

    /* eslint-disable-next-line @typescript-eslint/await-thenable */
    await dispatch({
      type: UPDATE_USER_ATTRIBUTES.ACTION,
      payload: appAuthenticator.updateUserAttributes(user, attributes),
      meta: {
        flag: PROMISE_REQUEST
      }
    });

    return getAndStoreUserAttributes(user)(dispatch);
  };
};

export const verifyUserAttributes = (attribute: string) => {
  return async (dispatch: ThunkDispatch<StoreState, unknown, Action>) => {
    const user: AuthenticatorUser = await appAuthenticator.getCurrentAuthenticatedUser();

    /* eslint-disable-next-line @typescript-eslint/await-thenable */
    await dispatch({
      type: VERIFY_USER_ATTRIBUTES.ACTION,
      payload: appAuthenticator.verifyUserAttribute(user, attribute),
      meta: {
        flag: PROMISE_REQUEST
      }
    });

    return getAndStoreUserAttributes(user)(dispatch);
  };
};

export const verifyUserAttributesSubmit = (attribute: string, code: string) => {
  return async (dispatch: ThunkDispatch<StoreState, unknown, Action>) => {
    const user: AuthenticatorUser = await appAuthenticator.getCurrentAuthenticatedUser();

    /* eslint-disable-next-line @typescript-eslint/await-thenable */
    await dispatch({
      type: VERIFY_USER_ATTRIBUTES_SUBMIT.ACTION,
      payload: appAuthenticator.verifyCurrentUserAttributeSubmit(attribute, code),
      meta: {
        flag: PROMISE_REQUEST
      }
    });

    return getAndStoreUserAttributes(user)(dispatch);
  };
};

export const setPreferredMFAType = (type = 'SMS') => {
  return async (dispatch: ThunkDispatch<StoreState, unknown, Action>) => {
    const user: AuthenticatorUser = await appAuthenticator.getCurrentAuthenticatedUser();

    /* eslint-disable-next-line @typescript-eslint/await-thenable */
    await dispatch({
      type: SET_PREFERRED_MFA_TYPE.ACTION,
      payload: appAuthenticator.setPreferredMFA(user, type),
      meta: {
        flag: PROMISE_REQUEST
      }
    });

    return getAndStoreUserMfa({ ...user, preferredMFA: type })(dispatch);
  };
};

/**
 * Updates the user password.
 * @returns {ThunkDispatch<StoreState, unknown, Action>} thunk dispatch function
 */
export const updateUserPassword = (oldPassword: string, newPassword: string) => {
  return async (dispatch: ThunkDispatch<StoreState, unknown, Action>) => {
    const user: AuthenticatorUser = await appAuthenticator.getCurrentAuthenticatedUser();

    /* eslint-disable-next-line @typescript-eslint/await-thenable */
    await dispatch({
      type: UPDATE_USER_ATTRIBUTES.ACTION,
      payload: appAuthenticator.changePassword(user, oldPassword, newPassword),
      meta: {
        flag: PROMISE_REQUEST
      }
    });

    return;
  };
};

/**
 * Updates an specific user atribute.
 * @param {string} key
 * @param {StorageDataValue} value
 * @returns {ThunkDispatch<StoreState, unknown, Action>} thunk dispatch function
 */
export const updateUserAttribute = (key: string, value?: StorageDataValue) => {
  return async (dispatch: ThunkDispatch<StoreState, unknown, Action>) =>
    updateUserAttributes({ [key]: value })(dispatch);
};
